import { useState, useRef, useMemo } from "react";

import classnames from "classnames";
import { isNotEmpty, notEquals } from "neetocist";
import useOnClickOutside from "neetocommons/react-utils/useOnClickOutside";
import useStateWithDependency from "neetocommons/react-utils/useStateWithDependency";
import PropTypes from "prop-types";
import { isEmpty } from "ramda";

import Breadcrumbs from "components/Breadcrumbs";

import EditName from "./EditName";
import "./rename.scss";
import ViewName from "./ViewName";

const Rename = ({
  value,
  onRename,
  onClone,
  onArchiveStatusToggle,
  onDisableStatusToggle,
  onDelete,
  isArchived,
  isDisabled,
  isOpen = false,
  isLoading = false,
  placeholder = "",
  hideMenu = false,
  disabled = false,
  dropdownItems = [],
  breadcrumbs = [],
  textProps = {},
  inputProps = {},
  submitButtonProps = {},
  cancelButtonProps = {},
  className = "",
  allowEmptySubmission = false,
  defaultName = "",
  formatText,
  dropdownButtonProps = {},
  dropdownProps = {},
  secondaryInfo = "",
}) => {
  const [name, setName] = useStateWithDependency(value ?? "");
  const [editMode, setEditMode] = useState(isOpen);
  const renameRef = useRef(null);

  const initialName = useMemo(() => name, [editMode]);

  useOnClickOutside(
    renameRef,
    () =>
      editMode &&
      handleClose({ saveOnOutsideClick: notEquals(name, initialName) })
  );

  const handleRename = (event, name) => {
    event?.stopPropagation();
    const newName = allowEmptySubmission && isEmpty(name) ? defaultName : name;
    setName(newName);
    onRename?.(newName);
    setEditMode(false);
  };

  const handleClose = ({ event = null, saveOnOutsideClick = false }) => {
    event?.stopPropagation();
    saveOnOutsideClick ? handleRename(event, name) : setName(value);
    setEditMode(false);
  };

  const handleRenameClick = event => {
    event.stopPropagation();
    !disabled && setEditMode(true);
  };

  const editNameProps = {
    allowEmptySubmission,
    cancelButtonProps,
    handleClose,
    handleRename,
    inputProps,
    isLoading,
    name,
    placeholder,
    setName,
    submitButtonProps,
    editMode,
  };

  const viewNameProps = {
    disabled,
    dropdownItems,
    formatText,
    handleRenameClick,
    hideMenu,
    name,
    isArchived,
    onArchiveStatusToggle,
    isDisabled,
    onDisableStatusToggle,
    onClone,
    onDelete,
    textProps,
    dropdownButtonProps,
    dropdownProps,
  };

  return (
    <div
      ref={renameRef}
      className={classnames(
        "neeto-molecules-rename flex min-w-0 max-w-md flex-col items-start md:flex-row md:items-center",
        className
      )}
    >
      {isNotEmpty(breadcrumbs) && (
        <Breadcrumbs
          {...{ breadcrumbs }}
          hasTrailingSeparator
          className="neeto-molecules-rename-breadcrumbs"
        />
      )}
      <div className="relative flex max-w-full items-center gap-x-2">
        {editMode ? (
          <EditName {...editNameProps} />
        ) : (
          <ViewName {...viewNameProps} />
        )}
        {isNotEmpty(secondaryInfo) && !editMode && (
          <div className="neeto-molecules-rename__secondary-info neeto-ui-text-gray-600 neeto-ui-bg-white absolute left-0 top-full ml-2 max-w-full -translate-y-1/2 truncate text-xs leading-tight">
            {secondaryInfo}
          </div>
        )}
      </div>
    </div>
  );
};

Rename.propTypes = {
  /**
   * To provide additional classes to the component.
   */
  className: PropTypes.string,
  /**
   * To allow empty submission.
   */
  allowEmptySubmission: PropTypes.bool,
  /**
   * Default value to be used on empty submission.
   */
  defaultName: PropTypes.string,
  /**
   * To specify the text to be displayed in the component.
   */
  value: PropTypes.string,
  /**
   * To specify the function to be triggered when renamed.
   */
  onRename: PropTypes.func,
  /**
   * To specify the function to be triggered when Clone menu is clicked. The menu will be hidden if this prop is not set.
   */
  onClone: PropTypes.func,
  /**
   * To specify the function to be triggered when Archive menu is clicked. The menu will be hidden if this prop is not set.
   */
  onArchiveStatusToggle: PropTypes.func,
  /**
   * To specify the function to be triggered when Enable/Disable menu is clicked. The menu will be hidden if this prop is not set.
   */
  onDisableStatusToggle: PropTypes.func,
  /**
   * To specify whether the entity is archived or not. The text in the menu will be changed based on this to Archive or Unarchive.
   */
  isArchived: PropTypes.bool,
  /**
   * To specify whether the entity is disabled or not. The text in the menu will be changed based on this to Enable or Disable.
   */
  isDisabled: PropTypes.bool,
  /**
   * To specify the function to be triggered when Delete menu is clicked. The menu will be hidden if this prop is not set.
   */
  onDelete: PropTypes.func,
  /**
   * To specify whether edit mode is enabled or not.
   */
  isOpen: PropTypes.bool,
  /**
   * To specify whether the edit buttons are in loading state or not.
   */
  isLoading: PropTypes.bool,
  /**
   * To specify the placeholder for the input component in edit mode.
   */
  placeholder: PropTypes.string,
  /**
   * To specify whether the dropdown menu should be hidden or not.
   */
  hideMenu: PropTypes.bool,
  /**
   * To specify whether the rename option in dropdown should be enabled. If it is the only option in dropdown, the menu will be hidden.
   */
  disabled: PropTypes.bool,
  /**
   * To specify additional items for the dropdown menu.
   */
  dropdownItems: PropTypes.array,
  /**
   * To specify the breadcrumbs if used in the title of `Header` component.
   */
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.string,
    })
  ),
  /**
   * To override the properties of the Typography component that displays the `value`.
   */
  textProps: PropTypes.object,
  /**
   * To override the properties of the Input component in edit mode.
   */
  inputProps: PropTypes.object,
  /**
   * To format the value to display when not in edit mode.
   */
  formatText: PropTypes.func,
  /**
   * To override the properties of the submit button.
   */
  submitButtonProps: PropTypes.object,
  /**
   * To override the properties of the cancel button.
   */
  cancelButtonProps: PropTypes.object,
  /**
   * To display secondary information below the rename input.
   */
  secondaryInfo: PropTypes.string,
};

export default Rename;
